const GROUP_SELECTORS = ['#header-group', '#MainContent', '.page-wrapper > footer'];
const STATE_CLASSES = ['section--next-rounded', 'section--first-rounded', 'section--last-rounded'];
const HEADER_BRIDGE_CLASS = 'section-stack--header-bridge';

/**
 * @param {Element} wrapper
 * @returns {HTMLElement | null}
 */
function getSectionSurface(wrapper) {
  return wrapper.querySelector(
    ':scope > .section, :scope > .hero, :scope > .header, :scope > [data-section-stack-surface]'
  );
}

/**
 * Reads a background color from inline styles only — no getComputedStyle —
 * so this never forces layout after class mutations.
 *
 * @param {Element} wrapper
 * @param {HTMLElement} surface
 * @returns {string}
 */
function getSectionBackground(wrapper, surface) {
  if (surface.classList.contains('header')) {
    return (
      surface.style.getPropertyValue('--color-background-bottom-row').trim() ||
      surface.style.getPropertyValue('--color-background-top-row').trim()
    );
  }

  const background = wrapper.querySelector(':scope > .section-background');
  if (background instanceof HTMLElement) {
    return (
      background.style.backgroundColor ||
      background.style.getPropertyValue('--color-background').trim()
    );
  }

  return surface.style.backgroundColor || surface.style.getPropertyValue('--color-background').trim();
}

/**
 * Updates the classes that connect rounded sections across page groups.
 */
export function updateSectionStack() {
  const groups = GROUP_SELECTORS.map((selector) => document.querySelector(selector)).filter(Boolean);
  const groupSections = groups.map((group) =>
    Array.from(group.children).filter((child) => child.classList.contains('shopify-section'))
  );
  const allSections = groupSections.flat();
  const orderedSurfaces = allSections.map(getSectionSurface);

  /** @type {{ surface: HTMLElement, className: string }[]} */
  const classAdds = [];
  /** @type {{ wrapper: HTMLElement, underlay: string }[]} */
  const headerBridges = [];

  orderedSurfaces.forEach((surface, index) => {
    if (!surface?.classList.contains('section--rounded')) return;

    const previousSurface = orderedSurfaces[index - 1];
    const previousIsTransparentHeader =
      previousSurface instanceof HTMLElement && previousSurface.hasAttribute('transparent');

    if (previousSurface && !previousIsTransparentHeader) {
      classAdds.push({ surface: previousSurface, className: 'section--next-rounded' });
    }

    const wrapper = allSections[index];
    const previousWrapper = allSections[index - 1];
    const bridgesHeaderToMain =
      previousWrapper?.parentElement?.id === 'header-group' && wrapper?.parentElement?.id === 'MainContent';

    if (
      bridgesHeaderToMain &&
      wrapper instanceof HTMLElement &&
      previousSurface &&
      previousWrapper &&
      !previousIsTransparentHeader
    ) {
      headerBridges.push({
        wrapper,
        underlay: getSectionBackground(previousWrapper, previousSurface),
      });
    }
  });

  const headerSurfaces = (groupSections[0] || []).map(getSectionSurface).filter(Boolean);
  const roundedHeaderSurfaces = headerSurfaces.filter((surface) => surface.classList.contains('section--rounded'));

  if (headerSurfaces[0]?.classList.contains('section--rounded')) {
    classAdds.push({ surface: headerSurfaces[0], className: 'section--first-rounded' });
  }

  const lastRoundedHeader = roundedHeaderSurfaces.at(-1);
  if (lastRoundedHeader) {
    classAdds.push({ surface: lastRoundedHeader, className: 'section--last-rounded' });
  }

  // All DOM writes happen after the reads above so we never force a reflow.
  for (const wrapper of allSections) {
    const surface = getSectionSurface(wrapper);
    if (surface) surface.classList.remove(...STATE_CLASSES);

    if (wrapper instanceof HTMLElement) {
      wrapper.classList.remove(HEADER_BRIDGE_CLASS);
      wrapper.style.removeProperty('--section-stack-underlay');
    }
  }

  for (const { surface, className } of classAdds) {
    surface.classList.add(className);
  }

  for (const { wrapper, underlay } of headerBridges) {
    wrapper.classList.add(HEADER_BRIDGE_CLASS);
    if (underlay) wrapper.style.setProperty('--section-stack-underlay', underlay);
  }

  const footerSections = groupSections.at(-1) || [];
  footerSections.forEach((wrapper, index) => {
    if (wrapper instanceof HTMLElement) {
      /* Later footer sections sit on top so the previous background can tuck under
         a rounded top instead of painting a radius-sized band over the next section. */
      wrapper.style.zIndex = String(index + 1);
    }
  });
}

function initializeSectionStack() {
  updateSectionStack();

  if (!window.Shopify?.designMode) return;

  document.addEventListener('shopify:section:load', updateSectionStack);
  document.addEventListener('shopify:section:unload', updateSectionStack);
  document.addEventListener('shopify:section:reorder', updateSectionStack);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSectionStack, { once: true });
} else {
  initializeSectionStack();
}
